import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Upload, ArrowLeft } from "lucide-react";
import { useToast } from "../../context/ToastContext";
import { useLanguage } from "../../context/LanguageContext";

const BuyerIndividualCreateProfileForm = ({
  onSubmit,
  isLoading,
  formData,
  onGoBack,
}) => {
  const { showError } = useToast();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";
  const [eIdFrontImage, seteIdFrontImage] = useState(null);
  const [eIdBackImage, seteIdBackImage] = useState(null);
  const [fileErrors, setFileErrors] = useState({});

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const handleFileChange = (setFile, fileType) => (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showError(translate("auth.signup.fileSizeError"));
        return;
      }
      setFile(file);
      // Clear error for this file type when file is selected
      setFileErrors((prev) => ({
        ...prev,
        [fileType]: null,
      }));
    }
  };

  const handleFormSubmit = (data) => {
    // Validate required Emirates ID front image; back is optional
    const errors = {};
    if (!eIdFrontImage) {
      errors.eIdFrontImage = translate("auth.signup.emiratesIdFrontRequired");
    }

    if (Object.keys(errors).length > 0) {
      setFileErrors(errors);
      return;
    }

    // Clear any previous errors
    setFileErrors({});

    onSubmit({
      ...data,
      eIdFrontImage,
      eIdBackImage,
    });
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className='w-full h-full'
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* Header with back button */}
      <div
        className={`flex items-center mb-6 ${isRTL ? "flex-row-reverse" : ""}`}
      >
        <button
          type='button'
          onClick={onGoBack}
          className={`flex items-center text-gray-600 hover:text-gray-800 transition-colors ${
            isRTL ? "flex-row-reverse" : ""
          }`}
        >
          <ArrowLeft
            className={`w-4 h-4 ${isRTL ? "ml-2 rotate-180" : "mr-2"}`}
          />
          {translate("auth.signup.back")}
        </button>
      </div>

      <div className='space-y-3'>
        <h3 className='text-2xl capitalize md:text-[28px] font-bold mb-1 text-[#131313] text-center'>
          {translate("auth.signup.completeBuyerProfile")}
        </h3>
        <p className='text-[#13131399] capitalize pb-6 text-sm md:text-[16px] text-center'>
          {translate("auth.signup.additionalDetailsBuyerIndividual")}
        </p>
      </div>

      {/* Personal Details Section */}
      <div className='mb-6'>
        <h4 className='text-lg font-semibold text-gray-800 mb-4 border-b pb-2'>
          {translate("auth.signup.personalDetails")}
        </h4>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {/* Location */}
          <div className='mb-4'>
            <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
              {translate("auth.signup.location")}{" "}
              <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              placeholder={translate("auth.signup.locationPlaceholder")}
              className={`w-full ${
                isRTL ? "pr-4 pl-4" : "pl-4 pr-4"
              } py-3 border ${
                errors.location ? "border-red-500" : "border-[#13131399]"
              } rounded-lg bg-white focus:outline-none`}
              {...register("location", {
                required: translate("auth.signup.locationRequired"),
              })}
            />
            {errors.location && (
              <p className='mt-1 text-sm text-red-500'>
                {errors.location.message}
              </p>
            )}
          </div>

          {/* Identification Number */}
          <div className='mb-4'>
            <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
              {translate("auth.signup.emiratesIdNumber")}{" "}
              <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              placeholder={translate("auth.signup.emiratesIdNumberPlaceholder")}
              className={`w-full ${
                isRTL ? "pr-4 pl-4" : "pl-4 pr-4"
              } py-3 border ${
                errors.identificationNo
                  ? "border-red-500"
                  : "border-[#13131399]"
              } rounded-lg bg-white focus:outline-none`}
              {...register("identificationNo", {
                required: translate("auth.signup.emiratesIdRequired"),
                pattern: {
                  value: /^[0-9-]+$/,
                  message: translate("auth.signup.emiratesIdInvalid"),
                },
              })}
            />
            {errors.identificationNo && (
              <p className='mt-1 text-sm text-red-500'>
                {errors.identificationNo.message}
              </p>
            )}
          </div>

          {/* EID Expiry Date */}
          <div className='mb-4'>
            <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
              {translate("auth.signup.eidExpiryDate")}{" "}
              <span className='text-red-500'>*</span>
            </label>
            <input
              type='date'
              className={`w-full ${
                isRTL ? "pr-4 pl-4" : "pl-4 pr-4"
              } py-3 border ${
                errors.idExprDate ? "border-red-500" : "border-[#13131399]"
              } rounded-lg bg-white focus:outline-none`}
              {...register("idExprDate", {
                required: translate("auth.signup.eidExpiryDateRequired"),
              })}
            />
            {errors.idExprDate && (
              <p className='mt-1 text-sm text-red-500'>
                {errors.idExprDate.message}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Bank Details Section - Hidden for Individual Buyers */}
      {/* Individual buyers don't need bank details */}

      {/* Document Upload Section */}
      <div className='mb-6'>
        <h4 className='text-lg font-semibold text-gray-800 mb-4 border-b pb-2'>
          {translate("auth.signup.requiredDocuments")}
        </h4>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {/* Emirates ID / Passport / National ID ( Front ) */}
          <div className='mb-4'>
            <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
              {translate("auth.signup.emiratesIdFront")}{" "}
              <span className='text-red-500'>*</span>
            </label>
            <div
              className={`border-2 border-dashed bg-white rounded-lg p-4 text-center ${
                fileErrors.eIdFrontImage ? "border-red-500" : "border-gray-500"
              }`}
            >
              <input
                type='file'
                id='eIdFrontImage'
                accept='.jpeg,.jpg,.png,.pdf'
                onChange={handleFileChange(seteIdFrontImage, "eIdFrontImage")}
                className='hidden'
              />
              <label
                htmlFor='eIdFrontImage'
                className='cursor-pointer flex flex-col items-center'
              >
                <Upload className='w-6 h-6 text-gray-500 mb-2' />
                <span className='text-sm text-gray-500'>
                  {translate("auth.signup.fileFormats")}
                </span>
                <button
                  type='button'
                  onClick={() =>
                    document.getElementById("eIdFrontImage").click()
                  }
                  className='mt-2 px-4 py-2 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition-colors'
                >
                  {translate("auth.signup.browseFile")}
                </button>
                {eIdFrontImage && (
                  <p className='mt-2 text-sm text-green-600'>
                    ✓ {eIdFrontImage.name}
                  </p>
                )}
              </label>
            </div>
            {fileErrors.eIdFrontImage && (
              <p className='mt-1 text-sm text-red-500'>
                {fileErrors.eIdFrontImage}
              </p>
            )}
          </div>

          {/* Emirates ID / National ID ( Back ) - Optional */}
          <div className='mb-4'>
            <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
              {translate("auth.signup.emiratesIdBack")}{" "}
              <span className='text-red-500'>*</span>
            </label>
            <div
              className={`border-2 border-dashed bg-white rounded-lg p-4 text-center ${"border-gray-500"}`}
            >
              <input
                type='file'
                id='eIdBackImage'
                accept='.jpeg,.jpg,.png,.pdf'
                onChange={handleFileChange(seteIdBackImage, "eIdBackImage")}
                className='hidden'
              />
              <label
                htmlFor='eIdBackImage'
                className='cursor-pointer flex flex-col items-center'
              >
                <Upload className='w-6 h-6 text-gray-500 mb-2' />
                <span className='text-sm text-gray-500'>
                  {translate("auth.signup.fileFormats")}
                </span>
                <button
                  type='button'
                  onClick={() =>
                    document.getElementById("eIdBackImage").click()
                  }
                  className='mt-2 px-4 py-2 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition-colors'
                >
                  {translate("auth.signup.browseFile")}
                </button>
                {eIdBackImage && (
                  <p className='mt-2 text-sm text-green-600'>
                    ✓ {eIdBackImage.name}
                  </p>
                )}
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Submit button */}
      <button
        type='submit'
        disabled={isLoading}
        className={`w-full border border-[#F15A29] bg-white text-[#F15A29] py-3 rounded-lg hover:bg-[#F15A29] hover:text-white transition-all duration-200 font-medium mb-4 ${
          isLoading ? "opacity-90 cursor-not-allowed" : ""
        }`}
      >
        {isLoading
          ? translate("auth.signup.submitting")
          : translate("auth.signup.completeBuyerRegistration")}
      </button>
    </form>
  );
};

export default BuyerIndividualCreateProfileForm;
